import { isUndefined } from 'lodash';
import { forwardRef, KeyboardEvent, useEffect, useState } from 'react';
import cn from 'bem-cn';

import { Icon } from '../Icon';
import { Stack } from '../Stack';

import { SearchProps } from './Search.types';
import './Search.scss';

const b = cn('search');

export const Search = forwardRef<HTMLInputElement, SearchProps>(
    (
        {
            value: controlledValue,
            onSearch,
            placeholder = 'Поиск...',
            size = 'medium',
            className,
            showClearButton = true,
            expanded = false,
            secondary = false,
            ...props
        },
        ref,
    ) => {
        const [value, setValue] = useState(controlledValue ?? '');

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const next = e.target.value;

            setValue(next);
        };

        const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && value.trim()) {
                onSearch?.(value);
            }
        };

        const handleClear = (e: React.MouseEvent) => {
            e.stopPropagation();

            setValue('');
        };

        const handleClearMouseDown = (e: React.MouseEvent) => {
            // Предотвращаем потерю фокуса с input при клике на clear button
            e.preventDefault();
        };

        const handleContainerClick = () => {
            props.onFocus?.();

            if (ref && typeof ref !== 'function') {
                ref.current?.focus();
            }
        };

        const showClear = showClearButton && value.trim().length;

        useEffect(() => {
            if (!isUndefined(controlledValue)) {
                setValue(controlledValue);
            }
        }, [controlledValue]);

        return (
            <Stack
                direction="row"
                alignItems="center"
                className={b({ size, expanded, secondary }).mix(className)}
                onClick={handleContainerClick}
            >
                <Icon className={b('icon')} name="search" />
                <input
                    ref={ref}
                    name="search"
                    className={b('input')}
                    autoComplete="off"
                    autoFocus={expanded}
                    placeholder={placeholder}
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    {...props}
                />
                {Boolean(showClear) && (
                    <Icon
                        className={b('clear')}
                        name="close"
                        onClick={handleClear}
                        onMouseDown={handleClearMouseDown}
                    />
                )}
            </Stack>
        );
    },
);

Search.displayName = 'Search';
